/**
 * Parametrage.js — Paramétrage du Planning d'Actions
 * Refonte complète — Design System SEIZO
 *
 * Charte : Sofia Pro / Nunito | #002335 | #00B0FF | #F4F7F9
 * Onglets :
 *   1. Général   — nom du planning
 *   2. Structure — colonnes + lignes (drag & drop, ajout/suppression inline)
 *   3. Statuts   — drag & drop, couleur, icône
 */
import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../../../stores/Context';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import PaletteCouleurs from '../../PaletteCouleurs';
import IconeStatut from './Icone/IconeStatut';
import axios from 'axios';
import styles from './Parametrage.module.scss';

// ── Icônes SVG ────────────────────────────────────────────────────────────────
const IcoArrowLeft = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <polyline points="15 18 9 12 15 6"/>
    </svg>
);
const IcoGrip = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <circle cx="9" cy="5" r="1.2"/><circle cx="15" cy="5" r="1.2"/>
        <circle cx="9" cy="12" r="1.2"/><circle cx="15" cy="12" r="1.2"/>
        <circle cx="9" cy="19" r="1.2"/><circle cx="15" cy="19" r="1.2"/>
    </svg>
);
const IcoTrash = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
        <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
    </svg>
);
const IcoPlus = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
);
const IcoCheck = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);
const IcoChevron = ({ open }) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
        style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
        <polyline points="6 9 12 15 18 9"/>
    </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────
const ICONS = ['Rond', 'Check', 'Reporte', 'Alerte'];
const reorder = (list, from, to) => {
    const r = [...list];
    const [removed] = r.splice(from, 1);
    r.splice(to, 0, removed);
    return r;
};
const randomColor = () => {
    const palette = ['#00B0FF','#66BE63','#FF9436','#F82121','#9C6ADE','#F8D541','#A8B0B3'];
    return palette[Math.floor(Math.random() * palette.length)];
};

// ── Onglet Général ────────────────────────────────────────────────────────────
const OngletGeneral = observer(({ onSaved }) => {
    const { PlanningActionsStore } = useStores();
    const [nom, setNom]       = useState(PlanningActionsStore.structure?.nom || '');
    const [saving, setSaving] = useState(false);
    const [done, setDone]     = useState(false);
    const [err, setErr]       = useState('');

    const save = async () => {
        if (!nom.trim()) { setErr('Le nom ne peut pas être vide.'); return; }
        setSaving(true); setErr('');
        try {
            await axios.put(
                `${process.env.REACT_APP_API_URL}/api/PA/saveParametrage/${PlanningActionsStore.idPlanning}`,
                { nomPlanning: nom, statuts: PlanningActionsStore.statuts },
                { headers: { Authorization: `Bearer ${PlanningActionsStore._token}` } }
            );
            setDone(true);
            setTimeout(() => { setDone(false); if (onSaved) onSaved(); }, 1200);
        } catch (e) {
            setErr(e.response?.data?.message || 'Erreur lors de la sauvegarde.');
        } finally { setSaving(false); }
    };

    return (
        <div className={styles.ongletContent}>
            <div className={styles.sectionCard}>
                <div className={styles.sectionCardTitle}>Nom du planning</div>
                <div className={styles.sectionCardSubtitle}>Identifiant affiché dans le widget et les exports</div>
                <input
                    className={styles.fieldInput}
                    type="text"
                    value={nom}
                    onChange={e => setNom(e.target.value)}
                    placeholder="Ex : Suivi de projet Q3"
                />
                {err && <div className={styles.errorMsg}>{err}</div>}
            </div>
            <div className={styles.footerActions}>
                <button
                    className={`${styles.saveBtn} ${done ? styles.saveBtnSuccess : ''}`}
                    onClick={save}
                    disabled={saving}
                >
                    {done ? <><IcoCheck /> Enregistré</> : saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
            </div>
        </div>
    );
});

// ── Onglet Structure ──────────────────────────────────────────────────────────
const OngletStructure = observer(() => {
    const { PlanningActionsStore } = useStores();
    const colonnes = PlanningActionsStore.structure?.colonnes || [];
    const lignes   = PlanningActionsStore.structure?.lignes   || [];
    const [expandedCol, setExpandedCol] = useState(null);

    // Colonnes
    const addColonne = () => {
        PlanningActionsStore.addColumnToDatabase(
            { libelle: 'Nouvelle colonne', couleur_fond: '#00B0FF', couleur_txt: '#FFFFFF' },
            colonnes.length
        );
    };
    const deleteColonne = (id) => PlanningActionsStore.deleteColumnToDatabase(id);
    const updateColonne = (id, field, value) => {
        const col = colonnes.find(c => c.id === id);
        if (!col) return;
        PlanningActionsStore.updateColumnData(
            id,
            field === 'libelle'      ? value : col.libelle,
            field === 'couleur_fond' ? value : col.couleur_fond,
            field === 'couleur_txt'  ? value : col.couleur_txt
        );
    };
    const onDragEndColonnes = (result) => {
        if (!result.destination) return;
        const reordered = reorder(colonnes, result.source.index, result.destination.index);
        PlanningActionsStore.setStructureColonnes(reordered);
    };

    // Sous-colonnes
    const addSousColonne = (colId) => {
        PlanningActionsStore.addSubColumnToDatabase({ libelle: 'Sous-colonne' }, colId);
    };
    const deleteSousColonne = (id) => PlanningActionsStore.deleteSubColumnToDatabase(id);
    const updateSousColonne = (id, libelle) => PlanningActionsStore.updateSubColumnData(id, libelle);

    // Lignes
    const addLigne = () => {
        PlanningActionsStore.addLineToDatabase(
            { libelle: 'Nouvelle ligne', couleur_fond: '#D7F3FF', couleur_txt: '#002335' },
            lignes.length
        );
    };
    const deleteLigne = (id) => PlanningActionsStore.deleteLineToDatabase(id);
    const updateLigne = (id, field, value) => {
        const l = lignes.find(x => x.id === id);
        if (!l) return;
        PlanningActionsStore.updateRowData(
            id,
            field === 'libelle'      ? value : l.libelle,
            field === 'couleur_fond' ? value : l.couleur_fond,
            field === 'couleur_txt'  ? value : l.couleur_txt
        );
    };
    const onDragEndLignes = (result) => {
        if (!result.destination) return;
        const reordered = reorder(lignes, result.source.index, result.destination.index);
        PlanningActionsStore.setStructureLignes(reordered);
    };

    return (
        <div className={styles.ongletContent}>
            {/* ── Colonnes ──────────────────────────────────────────── */}
            <div className={styles.sectionCard}>
                <div className={styles.sectionCardHeader}>
                    <div>
                        <div className={styles.sectionCardTitle}>Colonnes</div>
                        <div className={styles.sectionCardSubtitle}>Étapes ou périodes du planning — glissez pour réordonner</div>
                    </div>
                    <button className={styles.addBtn} onClick={addColonne}>
                        <IcoPlus /> Ajouter
                    </button>
                </div>
                <DragDropContext onDragEnd={onDragEndColonnes}>
                    <Droppable droppableId="colonnes">
                        {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps} className={styles.itemList}>
                                {colonnes.map((col, i) => (
                                    <Draggable key={col.id} draggableId={`col-${col.id}`} index={i}>
                                        {(prov, snap) => (
                                            <div
                                                ref={prov.innerRef}
                                                {...prov.draggableProps}
                                                className={`${styles.itemBlock} ${snap.isDragging ? styles.itemBlockDragging : ''}`}
                                            >
                                                <div className={styles.itemRow}>
                                                    <div className={styles.itemGrip} {...prov.dragHandleProps}>
                                                        <IcoGrip />
                                                    </div>
                                                    <div
                                                        className={styles.itemColorBar}
                                                        style={{ background: col.couleur_fond || '#00B0FF' }}
                                                    />
                                                    <input
                                                        className={styles.itemInput}
                                                        value={col.libelle}
                                                        onChange={e => updateColonne(col.id, 'libelle', e.target.value)}
                                                        onBlur={e => updateColonne(col.id, 'libelle', e.target.value)}
                                                        placeholder="Nom de la colonne"
                                                    />
                                                    <div className={styles.itemActions}>
                                                        <PaletteCouleurs
                                                            defaultColor={col.couleur_fond}
                                                            onColorChange={v => updateColonne(col.id, 'couleur_fond', v)}
                                                        />
                                                        <button
                                                            className={styles.itemExpandBtn}
                                                            onClick={() => setExpandedCol(expandedCol === col.id ? null : col.id)}
                                                            title="Sous-colonnes"
                                                        >
                                                            <IcoChevron open={expandedCol === col.id} />
                                                            {col.sous_colonnes?.length > 0 && (
                                                                <span className={styles.itemBadge}>{col.sous_colonnes.length}</span>
                                                            )}
                                                        </button>
                                                        <button
                                                            className={styles.itemDeleteBtn}
                                                            onClick={() => deleteColonne(col.id)}
                                                            title="Supprimer"
                                                        >
                                                            <IcoTrash />
                                                        </button>
                                                    </div>
                                                </div>
                                                {/* Sous-colonnes */}
                                                {expandedCol === col.id && (
                                                    <div className={styles.sousColonnesList}>
                                                        {col.sous_colonnes?.map(sc => (
                                                            <div key={sc.id} className={styles.sousColonneRow}>
                                                                <div className={styles.sousColonneIndent} />
                                                                <input
                                                                    className={styles.itemInput}
                                                                    value={sc.libelle}
                                                                    onChange={e => updateSousColonne(sc.id, e.target.value)}
                                                                    placeholder="Sous-colonne"
                                                                />
                                                                <button
                                                                    className={styles.itemDeleteBtn}
                                                                    onClick={() => deleteSousColonne(sc.id)}
                                                                >
                                                                    <IcoTrash />
                                                                </button>
                                                            </div>
                                                        ))}
                                                        <button
                                                            className={styles.addSousColonneBtn}
                                                            onClick={() => addSousColonne(col.id)}
                                                        >
                                                            <IcoPlus /> Ajouter une sous-colonne
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                                {colonnes.length === 0 && (
                                    <div className={styles.emptyState}>
                                        Aucune colonne — cliquez sur "Ajouter" pour commencer
                                    </div>
                                )}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>

            {/* ── Lignes ────────────────────────────────────────────── */}
            <div className={styles.sectionCard}>
                <div className={styles.sectionCardHeader}>
                    <div>
                        <div className={styles.sectionCardTitle}>Lignes</div>
                        <div className={styles.sectionCardSubtitle}>Axes du planning (équipes, projets, thèmes…) — glissez pour réordonner</div>
                    </div>
                    <button className={styles.addBtn} onClick={addLigne}>
                        <IcoPlus /> Ajouter
                    </button>
                </div>
                <DragDropContext onDragEnd={onDragEndLignes}>
                    <Droppable droppableId="lignes">
                        {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps} className={styles.itemList}>
                                {lignes.map((ligne, i) => (
                                    <Draggable key={ligne.id} draggableId={`ligne-${ligne.id}`} index={i}>
                                        {(prov, snap) => (
                                            <div
                                                ref={prov.innerRef}
                                                {...prov.draggableProps}
                                                className={`${styles.itemBlock} ${snap.isDragging ? styles.itemBlockDragging : ''}`}
                                            >
                                                <div className={styles.itemRow}>
                                                    <div className={styles.itemGrip} {...prov.dragHandleProps}>
                                                        <IcoGrip />
                                                    </div>
                                                    <div
                                                        className={styles.lignePreviewPill}
                                                        style={{
                                                            background: ligne.couleur_fond || '#D7F3FF',
                                                            color: ligne.couleur_txt || '#002335'
                                                        }}
                                                    >
                                                        {ligne.libelle || '—'}
                                                    </div>
                                                    <input
                                                        className={styles.itemInput}
                                                        value={ligne.libelle}
                                                        onChange={e => updateLigne(ligne.id, 'libelle', e.target.value)}
                                                        onBlur={e => updateLigne(ligne.id, 'libelle', e.target.value)}
                                                        placeholder="Nom de la ligne"
                                                    />
                                                    <div className={styles.itemActions}>
                                                        <span className={styles.colorHint}>Texte</span>
                                                        <PaletteCouleurs
                                                            defaultColor={ligne.couleur_txt}
                                                            onColorChange={v => updateLigne(ligne.id, 'couleur_txt', v)}
                                                        />
                                                        <span className={styles.colorHint}>Fond</span>
                                                        <PaletteCouleurs
                                                            defaultColor={ligne.couleur_fond}
                                                            onColorChange={v => updateLigne(ligne.id, 'couleur_fond', v)}
                                                        />
                                                        <button
                                                            className={styles.itemDeleteBtn}
                                                            onClick={() => deleteLigne(ligne.id)}
                                                            title="Supprimer"
                                                        >
                                                            <IcoTrash />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                                {lignes.length === 0 && (
                                    <div className={styles.emptyState}>
                                        Aucune ligne — cliquez sur "Ajouter" pour commencer
                                    </div>
                                )}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        </div>
    );
});

// ── Onglet Statuts ────────────────────────────────────────────────────────────
const OngletStatuts = observer(({ onSaved }) => {
    const { PlanningActionsStore } = useStores();
    const [statuts, setStatuts] = useState([...(PlanningActionsStore.statuts || [])]);
    const [saving, setSaving]   = useState(false);
    const [done, setDone]       = useState(false);
    const [err, setErr]         = useState('');

    const handleChange = (i, field, value) => {
        const next = [...statuts];
        next[i] = { ...next[i], [field]: value };
        setStatuts(next);
    };
    const handleAdd = () => setStatuts([...statuts, { libelle: '', icone: 'Rond', couleur: randomColor() }]);
    const handleRemove = (i) => {
        if (statuts.length <= 1) { setErr('Il doit y avoir au moins un statut.'); return; }
        setStatuts(statuts.filter((_, idx) => idx !== i));
    };
    const handleDragEnd = (result) => {
        if (!result.destination) return;
        setStatuts(reorder(statuts, result.source.index, result.destination.index));
    };
    const save = async () => {
        setErr('');
        for (const s of statuts) {
            if (!s.libelle.trim()) { setErr('Tous les libellés doivent être remplis.'); return; }
        }
        setSaving(true);
        try {
            await axios.put(
                `${process.env.REACT_APP_API_URL}/api/PA/saveParametrage/${PlanningActionsStore.idPlanning}`,
                { nomPlanning: PlanningActionsStore.structure?.nom, statuts },
                { headers: { Authorization: `Bearer ${PlanningActionsStore._token}` } }
            );
            await PlanningActionsStore.recupStatuts();
            setDone(true);
            setTimeout(() => { setDone(false); if (onSaved) onSaved(); }, 1200);
        } catch (e) {
            setErr(e.response?.data?.message || 'Erreur lors de la sauvegarde.');
        } finally { setSaving(false); }
    };

    return (
        <div className={styles.ongletContent}>
            <div className={styles.sectionCard}>
                <div className={styles.sectionCardHeader}>
                    <div>
                        <div className={styles.sectionCardTitle}>Statuts des tâches</div>
                        <div className={styles.sectionCardSubtitle}>Glissez pour réordonner — personnalisez couleur et icône</div>
                    </div>
                    <button className={styles.addBtn} onClick={handleAdd}>
                        <IcoPlus /> Ajouter
                    </button>
                </div>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="statuts">
                        {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps} className={styles.itemList}>
                                {statuts.map((s, i) => (
                                    <Draggable key={`statut-${i}`} draggableId={`statut-${i}`} index={i}>
                                        {(prov, snap) => (
                                            <div
                                                ref={prov.innerRef}
                                                {...prov.draggableProps}
                                                className={`${styles.itemBlock} ${snap.isDragging ? styles.itemBlockDragging : ''}`}
                                            >
                                                <div className={styles.itemRow}>
                                                    <div className={styles.itemGrip} {...prov.dragHandleProps}>
                                                        <IcoGrip />
                                                    </div>
                                                    <div className={styles.statutPreview}>
                                                        <IconeStatut icone={s.icone} couleur={s.couleur} />
                                                    </div>
                                                    <input
                                                        className={styles.itemInput}
                                                        type="text"
                                                        value={s.libelle}
                                                        onChange={e => handleChange(i, 'libelle', e.target.value)}
                                                        placeholder="Libellé du statut"
                                                    />
                                                    <div className={styles.itemActions}>
                                                        <select
                                                            className={styles.statutSelect}
                                                            value={s.icone}
                                                            onChange={e => handleChange(i, 'icone', e.target.value)}
                                                        >
                                                            {ICONS.map(ico => (
                                                                <option key={ico} value={ico}>{ico}</option>
                                                            ))}
                                                        </select>
                                                        <PaletteCouleurs
                                                            defaultColor={s.couleur}
                                                            onColorChange={c => handleChange(i, 'couleur', c)}
                                                        />
                                                        <button
                                                            className={styles.itemDeleteBtn}
                                                            onClick={() => handleRemove(i)}
                                                            title="Supprimer"
                                                        >
                                                            <IcoTrash />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
                {err && <div className={styles.errorMsg}>{err}</div>}
            </div>
            <div className={styles.footerActions}>
                <button
                    className={`${styles.saveBtn} ${done ? styles.saveBtnSuccess : ''}`}
                    onClick={save}
                    disabled={saving}
                >
                    {done ? <><IcoCheck /> Enregistré</> : saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
            </div>
        </div>
    );
});

// ── Composant principal ───────────────────────────────────────────────────────
const Parametrage = observer(() => {
    const { PlanningActionsStore } = useStores();
    const structure = PlanningActionsStore.structure;
    const [activeTab, setActiveTab] = useState('general');

    const TABS = [
        { id: 'general',   label: 'Général' },
        { id: 'statuts',   label: 'Statuts' },
    ];

    return (
        <div className={styles.root}>
            {/* ── Header ────────────────────────────────────────────────────── */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button
                        className={styles.backBtn}
                        onClick={() => PlanningActionsStore.changeView('Kanban')}
                        title="Retour au Kanban"
                    >
                        <IcoArrowLeft />
                    </button>
                    <div className={styles.headerDot} />
                    <div>
                        <div className={styles.headerSurtitle}>Paramètres</div>
                        <h1 className={styles.headerTitle}>{structure?.nom || 'Planning'}</h1>
                    </div>
                </div>
            </div>

            {/* ── Tabs ────────────────────────────────────────────────── */}
            <div className={styles.tabs}>
                {TABS.map(t => (
                    <button
                        key={t.id}
                        className={`${styles.tab} ${activeTab === t.id ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab(t.id)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* ── Contenu ─────────────────────────────────────────────── */}
            {activeTab === 'general'   && <OngletGeneral   onSaved={() => PlanningActionsStore.changeView('Kanban')} />}
             {activeTab === 'statuts'   && <OngletStatuts   onSaved={() => PlanningActionsStore.changeView('Kanban')} />}
        </div>
    );
});
export default Parametrage;
